import dynamic from "next/dynamic";
import { useCallback, useMemo, useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { ICONS } from "@artha/icons";
import { 
  Typography, 
  JobCardPublic, 
  JobCardSkeletonList, 
  Pagination, 
  Button 
} from "@artha/ui";
import { 
  formatSalary, 
  shouldHideSalaryBadge, 
  getOptimizedImageUrl,
  normalizeUsernameParam,
  getAppliedFiltersCount
} from "@/lib/utils";
import { formatCompactNumber } from "@artha/utils";
import type { 
  JobsResponseDto, 
  JobFiltersResponseDto, 
  JobItemDto 
} from "@artha/api-contracts";
import type { CreatorProfileForMyLink } from "@artha/types";
import { usePublicProfileJobs } from "@/modules/creator/hooks/usePublicProfileJobs";
import { usePoweredByExperiment } from "@/modules/creator/hooks/usePoweredByExperiment";
import { useAudienceAnalytics } from "@/lib/analytics";

const JobDetailsDrawer = dynamic(
  () => import("@/components/jobs/JobDetailsDrawer").then((mod) => mod.JobDetailsDrawer),
  { ssr: false }
);

const FiltersBottomSheet = dynamic(
  () => import("@artha/ui").then((mod) => mod.FiltersBottomSheet),
  { ssr: false }
);

type PublicJobFeedProps = {
  initialJobs: JobsResponseDto;
  username: string;
  creator: CreatorProfileForMyLink;
  filters?: JobFiltersResponseDto | null;
  isAuthenticated: boolean;
  ctaColor: string;
  jobsTopRef: React.RefObject<HTMLDivElement | null>;
  isPreviewMode?: boolean;
  onEarnClick?: () => void;
};

export function PublicJobFeed({
  initialJobs,
  username,
  creator,
  filters,
  isAuthenticated,
  ctaColor,
  jobsTopRef,
  isPreviewMode = false,
  onEarnClick,
}: PublicJobFeedProps) {
  const t = useTranslations("profile");
  const router = useRouter();
  const showPoweredBy = usePoweredByExperiment();
  const {
    trackJobView,
    trackJobApply,
    trackJobFilterJob,
  } = useAudienceAnalytics();
  
  const [selectedJob, setSelectedJob] = useState<JobItemDto | null>(null);

  const {
    jobs,
    totalJobs,
    totalPages,
    isLoading,
    isFetching,
    isError,
    error,
    currentPage,
    setCurrentPage,
    isFiltersOpen,
    setIsFiltersOpen,
    appliedFilters,
    setAppliedFilters,
    draftFilters,
    setDraftFilters,
    filtersForSheet,
    handleFiltersReset,
    preferenceFilterState,
  } = usePublicProfileJobs({
    initialJobs,
    username,
    creator,
    filters,
  });
  const appliedFiltersCount = useMemo(
    () => getAppliedFiltersCount(appliedFilters, preferenceFilterState),
    [appliedFilters, preferenceFilterState]
  );

  const getOptimizedLogoUrl = useCallback(
    (logo?: string | null) => getOptimizedImageUrl(logo, { width: 96, quality: 75 }),
    []
  );

  const normalizedUsername = useMemo(
    () => normalizeUsernameParam(username),
    [username],
  );

  const earnHref = useMemo(() => {
    const params = new URLSearchParams({
      utm_source: "creator",
      utm_medium: "footer",
      utm_campaign: "creator_referral",
      utm_content: normalizedUsername,
      ref: normalizedUsername,
    });
    return `https://artha.link/?${params.toString()}`;
  }, [normalizedUsername]);

  const getJobHref = useCallback(
    (job: JobItemDto) =>
      job.slug
        ? `/@${encodeURIComponent(normalizedUsername)}/jobs/${encodeURIComponent(job.slug)}`
        : job.url || "#",
    [normalizedUsername],
  );

  const handleJobSelect = useCallback(
    (job: JobItemDto) => {
      trackJobView(job);
      setSelectedJob(job);
    },
    [trackJobView],
  );

  const handleSearchRedirect = () => {
    const normalized = normalizeUsernameParam(username);
    const handle = `@${encodeURIComponent(normalized)}`;
    router.push(`/${handle}/search?search=1`);
  };

  const applyPortalContext = useMemo(
    () => ({
      portalId: creator.id,
      clientName: creator.displayName || creator.username,
      domainName: creator.username,
    }),
    [creator.displayName, creator.id, creator.username],
  );

  const optimizedAvatarUrl = useMemo(
    () => getOptimizedImageUrl(creator.avatarUrl, { width: 160, quality: 75 }),
    [creator.avatarUrl]
  );
  const hasJobs = jobs.length > 0;
  const showJobSkeletons = isLoading && !hasJobs;

  return (
    <>
      <section className="creator-jobs-section w-full space-y-2">
        <div ref={jobsTopRef} className="creator-jobs-header flex items-center gap-2">
          <Typography
            variant="h2"
          className="text-[18px] font-semibold text-black leading-none whitespace-nowrap"
        >
          {t("jobsTitle")}
          {typeof totalJobs === "number"
              ? ` (${formatCompactNumber(totalJobs)})`
              : ""}
        </Typography>
      </div>

        {showJobSkeletons && <JobCardSkeletonList count={3} />}
        
        {!showJobSkeletons && jobs.map((job, idx) => (
          <a
            key={`${job.ref_id || job.id || job.url || "job"}-${idx}`}
            href={getJobHref(job)}
            className="creator-job-card-link block w-full text-left"
            onClick={(event) => {
              event.preventDefault();
              handleJobSelect(job);
            }}
          >
            <JobCardPublic
              jobTitle={job.title}
              companyName={job.company}
              companyLogo={getOptimizedLogoUrl(job.logo)}
              location={job.location || ""}
              city={job.city}
              state={job.state}
              country={job.country}
              salary={formatSalary(job)}
              experience=""
              workMode={job.job_type ?? ""}
              skills={job.skills?.length ? job.skills : job.keywords}
              hideSalary={shouldHideSalaryBadge(job)}
              cpaValue={isAuthenticated ? job.cpa_value : undefined}
              cpcValue={isAuthenticated ? job.cpc_value : undefined}
            />
          </a>
        ))}

        {isError && (
          <div className="w-full flex justify-center">
            <Typography variant="lead" className="text-center text-destructive">
              {error?.message || t("unableToLoadJobs")}
            </Typography>
          </div>
        )}

        {!showJobSkeletons && !isError && !hasJobs && (
          <div className="w-full flex justify-center">
            <Typography variant="lead" className="text-center text-[#475467]">
              {t("noJobsFound")}
            </Typography>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex min-h-[44px] w-full justify-center overflow-hidden pt-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              disabled={isFetching}
              iconClassName="text-[#344054]"
            />
          </div>
        )}

        <Button
          asChild
          variant="outline"
          className="creator-earn-cta mt-2 w-full gap-2 rounded-[8px] border border-black bg-white px-3 py-5 text-[16px] font-medium shadow-[0px_4px_0px_0px_#000000]"
          style={{ color: ctaColor }}
        >
          <a href={earnHref} onClick={onEarnClick}>
            <ICONS.ARTHA_LINK_BADGE className="h-5 w-5" />
            {showPoweredBy ? t("poweredByCta") : t("earnCta")}
          </a>
        </Button>
      </section>

      {!isPreviewMode && (
        <div className="creator-floating-actions fixed bottom-6 left-1/2 z-40 w-[95%] max-w-[415px] -translate-x-1/2 px-4 pointer-events-none">
          <div className="w-full flex items-center gap-[10px] pointer-events-auto">
            <button
              type="button"
              onClick={handleSearchRedirect}
              className="creator-search-trigger flex flex-1 items-center gap-[10px] rounded-[8px] border border-black bg-white px-[15px] py-[10px] text-left shadow-[0px_4px_0px_0px_#000000] cursor-pointer"
            >
              <ICONS.SEARCH className="h-5 w-5 text-[#667085]" />
              <span className="flex-1 text-[14px] text-[#667085]">
                {t("searchPlaceholder")}
              </span>
            </button>
            <Button
              variant="outline"
              size="icon"
              className="creator-filter-trigger relative h-10 w-10 rounded-[8px] border-black bg-white p-[10px] shadow-[0px_4px_0px_0px_#000000]"
              aria-label={t("filterJobsAria")}
              onClick={() => setIsFiltersOpen(true)}
            >
              <ICONS.FILTER2 className="h-5 w-5 text-[#101828]" />
              {appliedFiltersCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 flex h-[18px] w-[18px] items-center justify-center rounded-full border border-black bg-[#FF60B5] text-[10px] font-bold text-white shadow-[1px_1px_0px_0px_#000000]">
                  {appliedFiltersCount}
                </span>
              )}
            </Button>
          </div>
        </div>
      )}

      {selectedJob ? (
        <JobDetailsDrawer
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          onSelectJob={handleJobSelect}
          onApply={(job) => trackJobApply(job)}
          portalContext={applyPortalContext}
          creatorName={creator.displayName || creator.username}
          creatorAvatarUrl={optimizedAvatarUrl || creator.avatarUrl}
        />
      ) : null}

      {isFiltersOpen ? (
        <FiltersBottomSheet
          isOpen={isFiltersOpen}
          onClose={() => setIsFiltersOpen(false)}
          filters={filtersForSheet ?? undefined}
          value={draftFilters}
          onChange={setDraftFilters}
          onApply={(next) => {
            trackJobFilterJob(appliedFilters, next);
            setAppliedFilters(next);
          }}
          onReset={handleFiltersReset}
          showEmployerFilters={false}
        />
      ) : null}
    </>
  );
}
